import React, { useState, useEffect } from "react";
import styles from "./Step2_Details.module.scss";
import RegistrationTypeCard from "../../ui/RegistrationTypeCard/RegistrationTypeCard";
import InputField from "../../ui/InputField/InputField";
import Button from "../../ui/Button/Button";
import InfoBox from "../../ui/InfoBox/InfoBox";
import RadioGroup from "../../ui/RadioGroup/RadioGroup";
import FileInput from "../../ui/FileInput/FileInput";

// Import icons
import textIcon from "../../../assets/icons/text.svg";
import galleryIcon from "../../../assets/icons/gallery.svg";
import flashCircleIcon from "../../../assets/icons/flash-circle.svg";

const Step2Details = ({ title, onNextStep, quizData }) => {
  // State for all form fields
  const [registrationType, setRegistrationType] = useState(
    quizData?.registrationType || (quizData?.trademarkName ? "name" : null)
  );
  const [trademarkName, setTrademarkName] = useState(quizData?.trademarkName || "");
  const [logoFile, setLogoFile] = useState(quizData?.logoFile || null);
  const [activityDescription, setActivityDescription] = useState(quizData?.activityDescription || "");
  const [isColor, setIsColor] = useState(quizData?.isColor || null);
  const [numApplicants, setNumApplicants] = useState(quizData?.numApplicants || null);

  // Sync state if quizData loads asynchronously
  useEffect(() => {
    if (quizData) {
      if (quizData.trademarkName) {
        setTrademarkName((prev) => prev || quizData.trademarkName);
        setRegistrationType((prev) => prev || quizData.registrationType || "name");
      }
      if (quizData.activityDescription) {
        setActivityDescription((prev) => prev || quizData.activityDescription);
      }
      if (quizData.registrationType) {
        setRegistrationType((prev) => prev || quizData.registrationType);
      }
      if (quizData.isColor) {
        setIsColor((prev) => prev || quizData.isColor);
      }
      if (quizData.numApplicants) {
        setNumApplicants((prev) => prev || quizData.numApplicants);
      }
    }
  }, [quizData]);

  // State for UI logic
  const [isFormValid, setIsFormValid] = useState(false);

  const showParametersBlock = activityDescription.trim() !== "";

  useEffect(() => {
    const validateForm = () => {
      if (!registrationType || !activityDescription.trim()) {
        return false;
      }

      if (
        (registrationType === "name" ||
          registrationType === "logo_with_text") &&
        !trademarkName.trim()
      ) {
        return false;
      }

      if (
        (registrationType === "logo_no_text" ||
          registrationType === "logo_with_text") &&
        !logoFile
      ) {
        return false;
      }

      if (showParametersBlock) {
        if (
          (registrationType === "logo_no_text" ||
            registrationType === "logo_with_text") &&
          !isColor
        ) {
          return false;
        }
        if (!numApplicants) {
          return false;
        }
      }

      return true;
    };

    setIsFormValid(validateForm());
  }, [
    registrationType,
    trademarkName,
    logoFile,
    activityDescription,
    isColor,
    numApplicants,
    showParametersBlock,
  ]);

  return (
    <div className={styles.container}>
      <h2 className={styles.largeHeader}>Про ваш бренд</h2>

      {quizData?.source && (
        <div style={{ marginBottom: "20px" }}>
          <InfoBox>
            ✨ Дані про бренд автоматично підтягнуто з {quizData.source === 'analysis' ? 'сервісу Аналізу' : 'сервісу Пошуку'} ТМ.
          </InfoBox>
        </div>
      )}

      <div className={styles.section}>
        <h3>Що саме реєструємо?</h3>
        <div className={styles.cardContainer}>
          <RegistrationTypeCard
            isSelected={registrationType === "name"}
            onClick={() => setRegistrationType("name")}
          >
            <img src={textIcon} alt="Text icon" />
            <span>Назва бренду</span>
          </RegistrationTypeCard>
          <RegistrationTypeCard
            isSelected={registrationType === "logo_no_text"}
            onClick={() => setRegistrationType("logo_no_text")}
          >
            <img src={galleryIcon} alt="Gallery icon" />
            <span>Логотип без тексту</span>
          </RegistrationTypeCard>
          <RegistrationTypeCard
            isSelected={registrationType === "logo_with_text"}
            onClick={() => setRegistrationType("logo_with_text")}
          >
            <img src={flashCircleIcon} alt="Flash icon" />
            <span>Логотип з текстом</span>
          </RegistrationTypeCard>
        </div>
      </div>

      <div
        className={`${styles.formBlock} ${registrationType ? styles.visible : ""}`}>
        {(registrationType === "name" ||
          registrationType === "logo_with_text") && (
          <div className={styles.section}>
            <InputField
              label="Назва торгової марки"
              placeholder="Brandr"
              value={trademarkName}
              onChange={(e) => setTrademarkName(e.target.value)}
            />
          </div>
        )}

        {(registrationType === "logo_no_text" ||
          registrationType === "logo_with_text") && (
          <div className={styles.section}>
            <FileInput
              label="Зображення / Логотип"
              allowedFormats="PNG, JPEG до 5 мб"
              onFileSelect={setLogoFile}
            />
          </div>
        )}

        <div className={styles.section}>
          <h3>Опишіть вашу діяльність</h3>
          <p>
            Це найважливіший крок. Чим детальніше ви опишете ваш бізнес та
            плани на майбутнє, тим точніше ми підберемо класи для його
            надійного захисту.
          </p>
          <InputField
            as="textarea"
            placeholder="Кав’ярня Brew&Joy — це заклад громадського харчування, що спеціалізується на приготуванні та продажу кавових напоїв, чаю, десертів та легких закусок."
            value={activityDescription}
            onChange={(e) => setActivityDescription(e.target.value)}
          />
        </div>

        <div
          className={`${styles.parametersBlock} ${
            showParametersBlock ? styles.visible : ""
          }`}
        >
          <div className={styles.section}>
            <h2 className={styles.largeHeader}>Параметри заявки</h2>
            <p>Параметри, що впливають на вартість держзборів</p>
          </div>

          {(registrationType === "logo_no_text" ||
            registrationType === "logo_with_text") && (
            <div className={styles.section}>
              <h3>Реєстрація ТМ буде у кольорі?</h3>
              <InfoBox>
                За подання кольорової ТМ державний збір збільшується. Якщо
                реєструвати чорно-білу ТМ, вона буде захищена у будь-якому
                кольорі.
              </InfoBox>
              <RadioGroup
                name="color"
                options={[
                  { value: "no", label: "Ні, чорно-біла (рекомендуємо*)" },
                  { value: "yes", label: "Так, кольорова" },
                ]}
                onSelect={setIsColor}
              />
            </div>
          )}

          <div className={styles.section}>
            <h3>Скільки буде заявників (власників) ТМ?</h3>
            <InfoBox>
              За додаткового заявника державний збір збільшується на
              30%.
            </InfoBox>
            <RadioGroup
              name="applicants"
              options={[
                { value: "one", label: "Один заявник" },
                { value: "multiple", label: "Два або більше" },
              ]}
              onSelect={setNumApplicants}
            />
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <Button
            variant="primary"
            onClick={() =>
              onNextStep({
                registrationType,
                trademarkName,
                logoFile,
                activityDescription,
                isColor,
                numApplicants,
              })
            }
            disabled={!isFormValid}
          >
            {quizData?.classIds?.length > 0 ? "Продовжити" : "Проаналізувати та підібрати класи"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step2Details;
